import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Link,
  Spinner,
  Text as ChakraText,
  useToken,
  VStack,
} from '@chakra-ui/react'
import React, { useContext, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useRouteMatch } from 'react-router-dom'
import { colors } from '../../../ui-kit'

import { UrlEndpointsSidebarGraphic } from '../../../graphics/UrlEndpoints'
import type { UrlEndpointsResponse } from '../../interfaces'
import { SideBar } from '../Base'
import { UserContext } from '../../providers'
import SearchBar from '../../components/SearchBar'
import UrlEndpointItem from '../../components/UrlEndpoints/UrlEndpointItem'
import { addLeadingSlash } from '../../../utils/urlHelpers'
import Shadow from '../../components/Shadow'
import PermissionGuard from '../../components/PermissionGuard'
import { PermissionsEnum } from '../../../utils'

interface Props {
  urlEndpoints?: UrlEndpointsResponse[]
  loadingUrlEndpoints: boolean
}

const UrlEndpointsSideMenu = (props: Props): React.ReactElement | null => {
  const { urlEndpoints, loadingUrlEndpoints } = props

  const { t } = useTranslation()
  const { path } = useRouteMatch()
  const { user } = useContext(UserContext)

  const [searchQuery, setSearchQuery] = useState<string>('')
  const [expandedEndpoints, setExpandedEndpoints] = useState<{
    [urlEndpointId: string]: boolean
  }>({})

  const backgroundColor = useToken('colors', ['appPaleGrey']) as string

  const searchQueryLowerCase = useMemo(
    () => searchQuery.toLowerCase(),
    [searchQuery],
  )
  const searchFilter = useMemo(
    () => (urlEndpoint: UrlEndpointsResponse) => {
      if (urlEndpoint.desc.toLowerCase().includes(searchQueryLowerCase)) {
        return true
      }
      if (urlEndpoint.isDefault) {
        if (
          user?.baseUrlForClientImageFetch
            .toLowerCase()
            .includes(searchQueryLowerCase)
        ) {
          return true
        }
      } else if (
        addLeadingSlash(urlEndpoint.urlPrefix)
          .toLowerCase()
          .includes(searchQueryLowerCase)
      ) {
        return true
      }
      if (urlEndpoint.cnameArr) {
        for (const cname of urlEndpoint.cnameArr) {
          if (cname.toLowerCase().includes(searchQueryLowerCase)) {
            return true
          }
        }
      }

      return false
    },
    [urlEndpoints, searchQueryLowerCase],
  )
  const filteredUrlEndpoints = urlEndpoints && urlEndpoints.length ? urlEndpoints?.filter(searchFilter) : []

  if (user === null) {
    return null
  }

  return (
    <SideBar
      heading={t('url_endpoints', 'URL Endpoints')}
      subheading={urlEndpoints ? `(${urlEndpoints.length})` : ''}
      button={
        <PermissionGuard
          permission={PermissionsEnum.SELF_INTEGRATION_MANAGE}
        >
          {
            (hasPermission: boolean) => (
              hasPermission ? (
                <Link
                  as={NavLink}
                  position="relative"
                  display="flex"
                  alignItems="center"
                  to={`${path}/new`}
                  _hover={{ textDecoration: 'none', outline: 'none' }}
                >
                  <Button>{t('plus_add_new')}</Button>
                </Link>
              ) : <></>
            )
          }
        </PermissionGuard>
      }
    >
      <SearchBar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        padding="1.5em 0.75em"
      />
      {!loadingUrlEndpoints ? (
        urlEndpoints && urlEndpoints.length > 0 ? (
          <Box
            overflow="auto"
            h="100%"
            position="relative"
            padding="0em 0.75em 1.5em"
          >
            <Shadow backgroundColor={backgroundColor} />
            <VStack spacing="1em" divider={<Divider />}>
              {filteredUrlEndpoints?.length ? (
                filteredUrlEndpoints.map((x) => (
                  <React.Fragment key={x.id}>
                    <UrlEndpointItem urlEndpoint={x} path={path} />
                  </React.Fragment>
                ))
              ) : (
                <ChakraText color="appBattleshipGrey">
                  {t('no_matches_found', 'No matches found')}
                </ChakraText>
              )}
            </VStack>
          </Box>
        ) : (
          <Flex
            flexDirection="column"
            textAlign="center"
            justifyContent="center"
            height="100%"
            width="full"
          >
            <UrlEndpointsSidebarGraphic
              height="101px"
              width="101px"
              margin="0 auto"
              marginBottom="1em"
            />
            <ChakraText
              color={colors.appBattleshipGrey}
              fontWeight="500"
              fontSize="1em"
              margin="0 auto"
            >
              {t('no_url_endpoint_added', 'No URL Endpoint added.')}
              <br />
              {t(
                'please_add_endpoint',
                'Please add a URL Endpoint to show list',
              )}
            </ChakraText>
          </Flex>
        )
      ) : (
        <Center height="100%">
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="appPaleGrey"
            color="appBlue"
            size="xl"
          />
        </Center>
      )}
    </SideBar>
  )
}

export default UrlEndpointsSideMenu
